import { config } from "configs/config";
import { invalidateForMutation, getPendingGet, setPendingGet } from "./apiCache";
import { isDemoMode, getDemoResponse } from "src/demo/demoApi";
import { loadDemoState, applyManualMatch, applyUnmatch, applyIgnore, saveDemoState, updateDemoState } from "src/demo/demoState";
import { InvoiceStatus } from "src/constants/invoice";
import { APP_INFO } from "src/config/appInfo";

// Logout callback registered by AuthProvider at runtime
let onUnauthorized: (() => void) | null = null;
export const registerUnauthorizedHandler = (fn: () => void) => {
    onUnauthorized = fn;
};

export class ApiError extends Error {
    status: number;
    body: any;
    constructor(status: number, message: string, body?: any) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.body = body;
    }
}

export async function apiRequest<T>(
    url: string,
    options: RequestInit = {},
    headers?: Record<string, string>,
): Promise<T> {
    const apiUrl = url.startsWith("/api") ? url : `/api${url}`;

    if (isDemoMode()) {
        const method = (options.method ?? "GET").toUpperCase();
        const path = apiUrl.replace(/^\/api/, "").split("?")[0];

        // Stateful demo endpoints (banksapi + invoices) live in localStorage
        const state = loadDemoState();

        if (method === "GET") {
            if (path === "/invoices") {
                return { invoices: state.invoices, total: state.invoices.length, filteredTotal: state.invoices.length } as any as T;
            }
            if (path === "/invoices/open") {
                const open = state.invoices
                    .filter((i) =>
                        i.status !== InvoiceStatus.PAID &&
                        i.status !== InvoiceStatus.WRITTEN_OFF &&
                        i.status !== 6 && // DELETED
                        !!i.sentDate,
                    )
                    .map((i) => ({
                        id: i.id,
                        readableId: i.readableId,
                        amount: i.amount,
                        recipientId: i.recipient?.id,
                        recipientName: i.recipient ? `${i.recipient.firstName} ${i.recipient.lastName}` : undefined,
                        patientId: i.patient?.id,
                        patientName: i.patient ? `${i.patient.firstName} ${i.patient.lastName}` : undefined,
                    }));
                return open as any as T;
            }
            const invMatch = path.match(/^\/invoices\/([^/]+)$/);
            if (invMatch) {
                const inv = state.invoices.find((i) => i.id === invMatch[1]) ?? state.invoices[0];
                return inv as any as T;
            }
            if (path === "/banksapi/status") {
                return state.bank as any as T;
            }
        }

        // Mutations we need for the interactive demo experience
        if (path === "/banksapi/sync" && method === "POST") {
            state.bank.lastSyncAt = new Date().toISOString();
            saveDemoState(state as any);
            return {} as T;
        }

        const matchPost = path.match(/^\/banksapi\/transactions\/([^/]+)\/match$/);
        if (matchPost && method === "POST") {
            const txId = decodeURIComponent(matchPost[1]);
            const body = typeof options.body === "string" ? JSON.parse(options.body) : (options.body as any) ?? {};
            applyManualMatch(txId, body.invoiceId, body.amount);
            return {} as T;
        }
        if (matchPost && method === "DELETE") {
            const txId = decodeURIComponent(matchPost[1]);
            applyUnmatch(txId);
            return {} as T;
        }

        const ignorePatch = path.match(/^\/banksapi\/transactions\/([^/]+)\/ignore$/);
        if (ignorePatch && method === "PATCH") {
            const txId = decodeURIComponent(ignorePatch[1]);
            applyIgnore(txId, true);
            return {} as T;
        }
        if (ignorePatch && method === "DELETE") {
            const txId = decodeURIComponent(ignorePatch[1]);
            applyIgnore(txId, false);
            return {} as T;
        }

        const invoicePatch = path.match(/^\/invoices\/([^/]+)$/);
        if (invoicePatch && (method === "PATCH" || method === "PUT")) {
            const invoiceId = decodeURIComponent(invoicePatch[1]);
            const body = typeof options.body === "string" ? JSON.parse(options.body) : (options.body as any) ?? {};

            const invPreview = loadDemoState().invoices.find((i) => i.id === invoiceId);
            if (
                invPreview &&
                (body.paidAmount != null || body.paymentDelta != null) &&
                !invPreview.sentDate
            ) {
                throw new ApiError(
                    400,
                    "Cannot record payments until the invoice has been sent.",
                    {},
                );
            }

            updateDemoState((s) => {
                const inv = s.invoices.find((i) => i.id === invoiceId);
                if (!inv) return s;

                if (body.amount != null) inv.amount = Number(body.amount);
                if (body.dunning != null) inv.dunning = Number(body.dunning);
                if (body.dunningPaused != null) inv.dunningPaused = !!body.dunningPaused;
                if (body.dueDate != null) inv.dueDate = body.dueDate ? new Date(body.dueDate).toISOString() : undefined;

                if (body.status != null) inv.status = Number(body.status);

                if (body.paidAmount != null) inv.paidAmount = Number(body.paidAmount);
                if (body.paymentDelta != null) inv.paidAmount = +(Number(inv.paidAmount ?? 0) + Number(body.paymentDelta)).toFixed(2);

                // Auto-status to PAID if fully covered
                if ((inv.paidAmount ?? 0) >= (inv.amount ?? 0) && (inv.amount ?? 0) > 0) {
                    inv.status = InvoiceStatus.PAID;
                } else if (inv.status === InvoiceStatus.PAID && (inv.paidAmount ?? 0) < (inv.amount ?? 0)) {
                    inv.status = inv.sentDate ? InvoiceStatus.SENT : InvoiceStatus.OPEN;
                }

                return { ...s };
            });

            return {} as T;
        }

        // Fallback: existing static demo mocks (and non-GET "silent success" behavior)
        const mock = getDemoResponse(apiUrl, method);
        return mock as T;
    }

    const method = (options.method ?? "GET").toUpperCase();

    // Concurrent callers wanting the same GET (e.g. two components mounting in the
    // same tick, both needing /banksapi/status) share one network request instead
    // of each firing their own — mutations are never deduped this way.
    if (method === "GET") {
        const inFlight = getPendingGet(apiUrl);
        if (inFlight) return inFlight as Promise<T>;
        const promise = performRequest<T>(apiUrl, method, options, headers);
        setPendingGet(apiUrl, promise);
        return promise;
    }

    return performRequest<T>(apiUrl, method, options, headers);
}

async function performRequest<T>(
    apiUrl: string,
    method: string,
    options: RequestInit,
    headers?: Record<string, string>,
): Promise<T> {
    const token = localStorage.getItem("token");
    const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
    const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;

    const FETCH_TIMEOUT_MS = 30_000;
    const timeoutCtrl = new AbortController();
    const timeoutId = window.setTimeout(() => timeoutCtrl.abort(), FETCH_TIMEOUT_MS);

    let response: Response;
    try {
        response = await fetch(`${config.baseUrl}${apiUrl}`, {
            headers: {
                ...(token && { Authorization: `Bearer ${token}` }),
                ...(doctorId && { "X-Doctor-Id": doctorId }),
                ...(praxisId && { "X-Praxis-Id": praxisId }),
                "X-Client-Version": APP_INFO.version,
                // A string body is JSON here (JSON.stringify) — without this header fetch sends it
                // as text/plain, express.json() skips it and the backend sees an empty body (the
                // shared-PC toggle silently saved false that way). FormData bodies set their own.
                ...(typeof options.body === "string" && { "Content-Type": "application/json" }),
                ...headers,
            },
            ...options,
            signal: timeoutCtrl.signal,
        });
    } catch (e: unknown) {
        window.clearTimeout(timeoutId);
        const name = e instanceof Error ? e.name : (e as { name?: string })?.name;
        if (name === "AbortError") {
            throw new ApiError(408, "Request timed out — is the API running and reachable?", {});
        }
        throw e;
    }
    window.clearTimeout(timeoutId);

    // Try to parse body once for better error handling.
    const contentType = response.headers.get("Content-Type") ?? "";
    const body: any = response.ok
        ? null
        : (contentType.includes("application/json")
            ? await response.json().catch(() => ({}))
            : { message: await response.text().catch(() => "") });

    if (response.status === 401) {
        // Only trigger logout on *our* auth failures (expired/invalid session).
        // Third-party proxy errors should not force a logout.
        const msg = body?.message ?? "";
        const isAuthFailure =
            typeof msg === "string" &&
            ["Missing or malformed token", "Invalid token", "Session revoked", "Session expired"].includes(msg);
        if (isAuthFailure) onUnauthorized?.();
        throw new ApiError(401, msg || "Unauthorized", body);
    }

    if (!response.ok) {
        throw new ApiError(
            response.status,
            body?.message ?? `Request failed with status ${response.status}`,
            body,
        );
    }

    if (method !== "GET") {
        invalidateForMutation(apiUrl);
    }

    window.dispatchEvent(new Event("api:activity"));
    return response.json();
}
